import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, HelpCircle } from "lucide-react";
import { openIntercomForSupport } from "@/utils/intercom";

interface FAQItem {
  question: string;
  answer: string | JSX.Element;
}

const FAQSection: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs: FAQItem[] = [
    {
      question: "What AI model does Shreds.AI use?",
      answer: (
        <>
          <p className="mb-4">
            Shreds.AI is powered by our proprietary, purpose-built model based on a new paradigm we call Small Logic Models (SLM).
          </p>
          <p className="mb-4">
            Unlike traditional Large Language Models (LLMs) that predict the next "word" or code token, our SLM generates high-level concept maps. This approach allows the AI to understand the underlying architecture, data flows, and logical relationships within a complex, multi-service project before writing a single line of code. This results in more coherent, robust, and context-aware backend systems.
          </p>
          <p className="mb-2">
            For highly specialized knowledge domains, our model can operate in a hybrid mode, consulting leading open-source models for specific information, such as:
          </p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Domain-specific legal or compliance requirements.</li>
            <li>Complex algorithms from specialized scientific or mathematical fields.</li>
          </ul>
        </>
      )
    },
    {
      question: "What technologies and frameworks does Shreds.AI support?",
      answer: (
        <>
          <p className="mb-4">
            Currently, Shreds.AI offers first-class, expert-level support for the Java / Spring Boot stack. This includes deep integration with its entire ecosystem:
          </p>
          <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
            <li>Build tools like Maven and Gradle.</li>
            <li>Data persistence with JPA/Hibernate.</li>
            <li>Testing frameworks like JUnit and Mockito.</li>
            <li>Core Spring modules (Security, MVC, Data, etc.) and compatible third-party libraries.</li>
          </ul>
          <p className="mb-4">
            Our philosophy is to ensure true mastery and excellence in one stack before expanding. This guarantees that any system generated for a supported stack meets the highest industry standards.
          </p>
          <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-400/30 rounded-lg p-4">
            <p className="text-blue-300 font-semibold">
              🚀 New stacks are released every few weeks! Make sure to create an account to be notified as new technology stacks are released.
            </p>
          </div>
        </>
      )
    },
    {
      question: "How do you ensure the generated code is production-ready?",
      answer: "We have a rigorous, automated validation cycle. Once code is generated, our AI writes its own integration tests to validate the functionality. The application is then run in an isolated sandbox environment that simulates a production setup, complete with necessary dependencies like databases and message brokers. Within this sandbox, the AI works autonomously to resolve any issues, iterating on the code until the application runs successfully and passes all tests. This closed-loop process of testing and self-correction guarantees the final code delivered is robust and production-ready."
    },
    {
      question: "Can I customize or modify the code generated by Shreds.AI?",
      answer: (
        <>
          <p className="mb-4">
            Absolutely. Shreds.AI is designed to augment your development team, not replace it. We integrate directly into a standard Git-based workflow.
          </p>
          <p className="mb-2">
            When Shreds.AI completes a task, it creates a Pull Request (PR) in your GitHub repository. From there, your team is in full control:
          </p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li><strong>Review and Comment:</strong> Your engineers can review the code and leave comments on the PR. Shreds.AI will parse these comments, understand the requested changes, and push updates to the same PR.</li>
            <li><strong>Directly Modify:</strong> If you prefer, you can pull the branch and modify the code yourself. Once you commit and push your changes, Shreds.AI will recognize the manual updates and incorporate them into its context for any future work on that module.</li>
          </ul>
        </>
      )
    },
    {
      question: "How do you handle the confidentiality of my source code?",
      answer: (
        <>
          <p className="mb-4">
            Code confidentiality and data security are foundational to Shreds.AI. We enforce strict security guardrails to protect your intellectual property.
          </p>
          <ol className="list-decimal list-inside ml-4 space-y-2">
            <li><strong>Ephemeral Code Handling:</strong> Your source code is never stored permanently on our servers. It is held temporarily in RAM during generation and testing and is immediately deleted once all operations are done.</li>
            <li><strong>Your Repository is the Source of Truth:</strong> The only persistent location for your code is your own private repository, which you control completely.</li>
            <li><strong>Anonymized Specification Learning:</strong> We securely store anonymized, high-level software specifications (e.g., architectural patterns, API structures) completely stripped of any proprietary business logic or identifying data. This data is used exclusively to allow you to navigate your own project and modify it.</li>
            <li><strong>Your Right to Deletion:</strong> At any time, whether your project is complete or you choose to stop using our service, you can request the complete and permanent deletion of all project specifications and architectural data from our systems.</li>
          </ol>
        </>
      )
    },
    {
      question: "How does Shreds.AI integrate with my team's existing development workflow?",
      answer: (
        <>
          <p className="mb-4">
            Shreds.AI was designed by senior engineers to slot seamlessly into modern Agile and DevOps workflows. It follows the natural development lifecycle:
          </p>
          <ol className="list-decimal list-inside ml-4 space-y-2 mb-4">
            <li><strong>Architecture & Requirements:</strong> ShredsAI defines the high-level architecture and requirements.</li>
            <li><strong>AI-Powered Task Breakdown:</strong> ShredsAI breaks down the architecture into granular specifications for each module, much like creating tickets in a project management tool (e.g., Jira).</li>
            <li><strong>Autonomous Development:</strong> The AI "picks up" these tickets after your validation and autonomously develops the code, tests, and configurations.</li>
            <li><strong>Pull Request for Review:</strong> The output is delivered as a standard Pull Request, the natural collaboration point for any engineering team.</li>
          </ol>
          <p>
            You can think of Shreds.AI as a team of highly efficient senior engineers working in parallel. This allows your human engineers to offload boilerplate development and focus on what truly matters: high-level architecture, complex problem-solving, and driving innovation.
          </p>
        </>
      )
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="relative py-12 md:py-16 overflow-hidden bg-gradient-to-br from-slate-900/50 via-gray-900/80 to-slate-800/50">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/6 w-72 h-72 bg-blue-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/6 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-8">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, margin: "-10%" }}
        >
          <motion.div
            className="flex items-center justify-center mb-6"
            initial={{ scale: 0.9 }}
            whileInView={{ scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center space-x-3 px-6 py-3 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-full border border-blue-400/20">
              <HelpCircle className="w-5 h-5 text-blue-400" />
              <span className="text-blue-300 font-medium">Got Questions?</span>
            </div>
          </motion.div>

          <motion.h2 
            className="text-5xl lg:text-6xl font-black text-white leading-tight mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Frequently Asked
            <br />
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
              Questions
            </span>
          </motion.h2>
          
          <motion.p 
            className="text-lg text-gray-300 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            Everything you need to know about ShredsAI
          </motion.p>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              className="relative group"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true, margin: "-5%" }}
            >
              <div className="relative bg-gray-900/40 backdrop-blur-sm border border-gray-700/50 rounded-2xl overflow-hidden group-hover:bg-gray-800/60 transition-all duration-300">
                <motion.div 
                  className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                />
                
                <button
                  onClick={() => toggleFAQ(index)}
                  className="relative z-10 w-full px-8 py-6 text-left flex items-center justify-between group-hover:text-white transition-colors duration-300"
                >
                  <h3 className="text-lg font-semibold text-gray-200 pr-8">
                    {faq.question}
                  </h3>
                  
                  <motion.div
                    animate={{ rotate: openIndex === index ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                      openIndex === index 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-700 text-gray-400 group-hover:bg-gray-600'
                    } transition-all duration-300`}
                  >
                    {openIndex === index ? (
                      <Minus className="w-4 h-4" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                  </motion.div>
                </button>

                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="relative z-10 px-8 pb-6">
                        <motion.div 
                          className="text-gray-300 leading-relaxed"
                          initial={{ y: -10, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          exit={{ y: -10, opacity: 0 }}
                          transition={{ duration: 0.3, delay: 0.1 }}
                        >
                          {faq.answer}
                        </motion.div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          viewport={{ once: true }}
        >
          <p className="text-gray-400 mb-4">Still have questions?</p>
          <motion.button
            className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-500 hover:to-purple-500 transition-all duration-300"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.98 }}
            onClick={openIntercomForSupport}
          >
            Contact Support
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
};

export default FAQSection;